import React, { useState, useCallback, useEffect, useRef } from 'react';
import './ResizablePane.css';

function ResizablePane({ children, minWidth = 420, maxWidth = 80, initialWidth = 40, isOpen }) {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const paneRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const rafRef = useRef(null);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = paneRef.current ? paneRef.current.offsetWidth : 0;
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const resize = useCallback(
    (e) => {
      if (!isResizing || !paneRef.current) return;

      e.preventDefault();

      // Use requestAnimationFrame for smooth updates
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const containerWidth = window.innerWidth;

        // Calculate the delta from where we started dragging
        const deltaX = e.clientX - startXRef.current;

        // Calculate new width in pixels, then convert to percentage
        const newWidthPx = startWidthRef.current + deltaX;
        const newWidth = (newWidthPx / containerWidth) * 100;

        // Calculate min width as percentage
        const minWidthPercent = (minWidth / containerWidth) * 100;

        // Constrain width between min and max
        const constrainedWidth = Math.min(Math.max(newWidth, minWidthPercent), maxWidth);
        setWidth(constrainedWidth);
      });
    },
    [isResizing, minWidth, maxWidth]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isResizing, resize, stopResizing]);

  // Store width in localStorage for persistence
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem('codePaneWidth', width.toString());
    }
  }, [width, isOpen]);

  // Load width from localStorage on mount
  useEffect(() => {
    const savedWidth = localStorage.getItem('codePaneWidth');
    if (savedWidth) {
      const parsedWidth = parseFloat(savedWidth);
      const containerWidth = window.innerWidth;
      const minWidthPercent = (minWidth / containerWidth) * 100;
      
      if (parsedWidth >= minWidthPercent && parsedWidth <= maxWidth) {
        setWidth(parsedWidth);
      }
    }
  }, [minWidth, maxWidth]);

  return (
    <>
      {/* Overlay to block iframe pointer events during resize */}
      <div className={`resize-overlay ${isResizing ? 'active' : ''}`} />

      <div
        ref={paneRef}
        className={`resizable-pane ${isOpen ? 'open' : ''} ${isResizing ? 'resizing' : ''}`}
        style={{
          width: isOpen ? `${width}%` : '0',
          minWidth: isOpen ? `${minWidth}px` : '0',
        }}
      >
        {isOpen && (
          <div
            className="resize-handle"
            onMouseDown={startResizing}
            title="Drag to resize"
          >
            <div className="resize-handle-line" />
          </div>
        )}
        {children}
      </div>
    </>
  );
}

export default ResizablePane;
